/**
 * JobAttachmentTable — table-style attachment picker for the Run Job sheet's
 * Files tab.
 *
 * Different visual treatment from ChatAttachmentPicker (chip-list, used in
 * the chat composer) but same PendingAttachment state shape, so callers can
 * swap visual without changing what they store. Upload happens in the
 * background as soon as files are picked; per-row status indicates whether
 * each file is still uploading, done, or failed.
 */

import { useRef, useState } from 'react';
import { Button } from '@/components/Button/Button';
import { useUploadAttachment } from '@/features/attachments';
import { t } from '@/lib/i18n';
import type { PendingAttachment } from '@/features/members/components/ChatAttachmentPicker';
import styles from './JobAttachmentTable.module.css';

interface JobAttachmentTableProps {
  attachments: PendingAttachment[];
  onChange: (updater: (current: PendingAttachment[]) => PendingAttachment[]) => void;
}

export function JobAttachmentTable({ attachments, onChange }: JobAttachmentTableProps) {
  const upload = useUploadAttachment({ embedSync: true });
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function handleFiles(filesList: FileList | null) {
    if (!filesList) return;
    setError(null);
    const files = Array.from(filesList);
    const additions: PendingAttachment[] = files.map((f) => ({
      localId: crypto.randomUUID(),
      name: f.name,
      size: f.size,
    }));
    onChange((current) => [...current, ...additions]);

    additions.forEach((p, idx) => {
      const file = files[idx];
      if (!file) return;
      upload
        .mutateAsync({ file })
        .then((res) => {
          onChange((current) =>
            current.map((c) =>
              c.localId === p.localId ? { ...c, attachment: res.attachment } : c,
            ),
          );
        })
        .catch((err) => {
          onChange((current) =>
            current.map((c) =>
              c.localId === p.localId
                ? { ...c, error: err instanceof Error ? err.message : 'Upload failed' }
                : c,
            ),
          );
        });
    });

    // Reset so the same file can be re-picked after a remove.
    if (inputRef.current) inputRef.current.value = '';
  }

  function remove(localId: string) {
    onChange((current) => current.filter((p) => p.localId !== localId));
  }

  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t('File')}</th>
            <th scope="col" className={styles.sizeCol}>
              {t('Size')}
            </th>
            <th scope="col" className={styles.statusCol}>
              {t('Status')}
            </th>
            <th scope="col" className={styles.actionCol}>
              <span className="sr-only">{t('Remove')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {attachments.length === 0 ? (
            <tr>
              <td colSpan={4} className={styles.empty}>
                {t('No files attached.')}
              </td>
            </tr>
          ) : (
            attachments.map((p) => (
              <tr key={p.localId}>
                <td className={styles.name}>{p.name}</td>
                <td className={styles.size}>{formatBytes(p.size)}</td>
                <td className={styles.status}>
                  {p.error ? (
                    <span className={styles.statusError} title={p.error}>
                      {t('Failed')}
                    </span>
                  ) : !p.attachment ? (
                    <span className={styles.statusPending}>{t('Uploading…')}</span>
                  ) : (
                    <span className={styles.statusReady}>{t('Ready')}</span>
                  )}
                </td>
                <td className={styles.action}>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => remove(p.localId)}
                    aria-label={t('Remove {name}', { name: p.name })}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className={styles.uploadRow}>
        <input
          ref={inputRef}
          type="file"
          multiple
          className={styles.hiddenInput}
          onChange={(e) => handleFiles(e.target.files)}
          aria-hidden="true"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          {t('Upload')}
        </Button>
      </div>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
